/**
 * Top-level scoring entry point — §7.
 *
 * scoreGame(state) → Result<GameState, string>
 *
 * Scoring order per player (§7):
 *   1. Wildlife scoring cards (§7.1)
 *   2. Habitat corridors (§7.2)
 *   3. Habitat majority bonuses (§7.3) — skipped when familySkipMajority
 *   4. Nature tokens (§7.4)
 *   5. Landmark effects (§7.5)
 *
 * Solo variant (§8): majority uses the solo table (+2 per habitat with size≥7).
 *
 * After all players are scored, §7.6 tiebreak determines winners.
 *
 * Pure — no I/O, no mutations.
 */

import { LANDMARK_CARDS_BY_ID } from "../data/landmark-cards";
import { WILDLIFE_SCORING_CARDS_BY_ID } from "../data/wildlife-scoring-cards";
import type { Result } from "../types/common";
import { err, ok } from "../types/common";
import type { GameState } from "../types/game-state";
import type { Habitat } from "../types/habitat";
import { HABITATS } from "../types/habitat";
import type { LandmarkCardDef } from "../types/landmark";
import type { PlayerScoreBreakdown, PlayerState } from "../types/player";
import type { Wildlife } from "../types/wildlife";
import type { CorridorOpts } from "./corridor";
import { corridorScoreForSize, findHabitatCorridors } from "./corridor";
import { applyLandmarkEffects } from "./landmark";
import type { PlayerCorridorInput } from "./majority";
import { scoreHabitatMajority } from "./majority";
import { scoreNatureTokens } from "./nature";
import { computeWinners } from "./tiebreak";
import { scoreWildlifeForPlayer } from "./wildlife";

// ---------------------------------------------------------------------------
// Corridor opts from landmark picks
// ---------------------------------------------------------------------------

/**
 * Derives CorridorOpts for a given habitat from the player's landmark picks.
 * G card → singleTileCounts=true; H card → minTwoTile=true.
 */
const corridorOptsForPlayer = (
  player: PlayerState,
  habitat: Habitat,
): CorridorOpts => {
  let singleTileCounts = false;
  let minTwoTile = false;

  for (const cardId of player.landmarkPicks) {
    const card = LANDMARK_CARDS_BY_ID[cardId];
    if (card === undefined) {
      continue;
    }
    if (
      card.effect.kind === "singleTileCorridorCounts" &&
      card.effect.habitat === habitat
    ) {
      singleTileCounts = true;
    }
    if (
      card.effect.kind === "minTwoTileCorridor" &&
      card.effect.habitat === habitat
    ) {
      minTwoTile = true;
    }
  }

  return { singleTileCounts, minTwoTile };
};

// ---------------------------------------------------------------------------
// scoreOnePlayer
// ---------------------------------------------------------------------------

/**
 * Computes the raw ScoreBreakdown for one player (wildlife + corridors + nature).
 * Majority and landmark effects are applied in a separate pass.
 *
 * Returns Err when a scoring card id is not found in the registry.
 */
const scoreOnePlayerBase = (
  player: PlayerState,
  scoringCardIds: Readonly<Record<string, string>>,
): Result<{
  breakdown: PlayerScoreBreakdown;
  largestCorridorByHabitat: Record<Habitat, number>;
}> => {
  // --- §7.1 Wildlife ---
  const wildlifeSpecies: readonly Wildlife[] = [
    "bear",
    "elk",
    "salmon",
    "hawk",
    "fox",
  ];

  const wildlifeByCard: Record<Wildlife, number> = {
    bear: 0,
    elk: 0,
    salmon: 0,
    hawk: 0,
    fox: 0,
  };

  for (const species of wildlifeSpecies) {
    const cardId = scoringCardIds[species];
    if (cardId === undefined) {
      return err(`ERR_UNKNOWN_CARD_REF: no scoring card for ${species}`);
    }
    const cardDef = WILDLIFE_SCORING_CARDS_BY_ID[cardId];
    if (cardDef === undefined) {
      return err(`ERR_UNKNOWN_CARD_REF: card "${cardId}" not in registry`);
    }
    wildlifeByCard[species] = scoreWildlifeForPlayer(
      player,
      cardDef.rule,
      species,
    );
  }

  // --- §7.2 Habitat corridors ---
  const habitatByType: Record<Habitat, number> = {
    forest: 0,
    mountain: 0,
    prairie: 0,
    wetland: 0,
    river: 0,
  };
  const largestCorridorByHabitat: Record<Habitat, number> = {
    forest: 0,
    mountain: 0,
    prairie: 0,
    wetland: 0,
    river: 0,
  };

  for (const habitat of HABITATS) {
    const opts = corridorOptsForPlayer(player, habitat);
    const corridors = findHabitatCorridors(player.board, habitat);
    if (corridors.length === 0) {
      continue;
    }
    const largestSize = Math.max(...corridors.map((c) => c.coords.length));
    const score = corridorScoreForSize(largestSize, opts);
    habitatByType[habitat] = score;
    largestCorridorByHabitat[habitat] = largestSize;
  }

  // --- §7.4 Nature ---
  const nature = scoreNatureTokens(player);

  const total =
    (Object.values(wildlifeByCard) as number[]).reduce((a, b) => a + b, 0) +
    (Object.values(habitatByType) as number[]).reduce((a, b) => a + b, 0) +
    nature;

  const breakdown: PlayerScoreBreakdown = {
    wildlifeByCard,
    habitatByType,
    habitatMajorityBonus: 0, // filled in later
    nature,
    landmarks: 0, // filled in later
    total,
  };

  return ok({ breakdown, largestCorridorByHabitat });
};

// ---------------------------------------------------------------------------
// scoreGame
// ---------------------------------------------------------------------------

export const scoreGame = (state: GameState): Result<GameState, string> => {
  const { players, ruleset, scoringCards } = state;
  const playerCount = ruleset.playerCount;
  const skipMajority = ruleset.modules.familySkipMajority === true;

  // Validate all scoring card ids up front
  const scoringCardIds: Record<string, string> = {
    bear: scoringCards.bear,
    elk: scoringCards.elk,
    salmon: scoringCards.salmon,
    hawk: scoringCards.hawk,
    fox: scoringCards.fox,
  };
  for (const [species, cardId] of Object.entries(scoringCardIds)) {
    if (WILDLIFE_SCORING_CARDS_BY_ID[cardId] === undefined) {
      return err(
        `ERR_UNKNOWN_CARD_REF: card "${cardId}" for ${species} not in registry`,
      );
    }
  }

  // --- Pass 1: base scores (wildlife + corridors + nature) ---
  type BaseResult = {
    breakdown: PlayerScoreBreakdown;
    largestCorridorByHabitat: Record<Habitat, number>;
  };
  const baseResults: BaseResult[] = [];

  for (const player of players) {
    const result = scoreOnePlayerBase(player, scoringCardIds);
    if (!result.ok) {
      return err(result.error);
    }
    baseResults.push(result.value);
  }

  // --- Pass 2: majority bonuses (§7.3) ---
  const majorityInputs: PlayerCorridorInput[] = players.map((p, i) => ({
    playerId: p.playerId,
    largestCorridorByHabitat: baseResults[i]!.largestCorridorByHabitat,
  }));

  // Per-habitat majority, summed per player
  const majorityBonusByPlayer = new Map<string, number>(
    players.map((p) => [p.playerId, 0]),
  );

  if (!skipMajority) {
    for (const habitat of HABITATS) {
      const habitatResults = scoreHabitatMajority(
        majorityInputs,
        playerCount,
        habitat,
      );
      for (const { playerId, bonus } of habitatResults) {
        majorityBonusByPlayer.set(
          playerId,
          (majorityBonusByPlayer.get(playerId) ?? 0) + bonus,
        );
      }
    }
  }

  // --- Pass 3: landmark effects (§7.5) ---
  const allLandmarkCards: readonly LandmarkCardDef[] =
    Object.values(LANDMARK_CARDS_BY_ID);

  // --- Assemble final breakdowns ---
  const finalPlayers = players.map((player, i) => {
    const base = baseResults[i]!;
    const majorityBonus = majorityBonusByPlayer.get(player.playerId) ?? 0;

    // Inject majority into breakdown before landmark pass
    const withMajority: PlayerScoreBreakdown = {
      ...base.breakdown,
      habitatMajorityBonus: majorityBonus,
      total: base.breakdown.total + majorityBonus,
    };

    // Apply landmark effects
    const finalBreakdown = applyLandmarkEffects(
      player,
      withMajority,
      allLandmarkCards,
    );

    return { ...player, scoreBreakdown: finalBreakdown };
  });

  // --- §7.6 Tiebreak ---
  const playerScores = finalPlayers.map((p) => ({
    playerId: p.playerId,
    total: p.scoreBreakdown!.total,
    natureTokens: p.natureTokens,
  }));

  const winners = computeWinners(playerScores);

  return ok({
    ...state,
    players: finalPlayers,
    winners,
  });
};
